// The size and the date of directories in a file list. A service does not
// tell them: the size is the sum of the sizes of the files under the
// directory, in all its sub-directories, and the date is the date of the
// newest of those files. Getting them means reading every directory under
// the ones listed, which takes a while, so what is known so far is shown
// while the scan goes on.

import * as filepath from "./filepath";
import type { FsDir } from "./fsentry";

export interface DirStat {
  // the sum of the sizes of the files found so far
  size: number;
  // the date of the newest file found so far, 0 if none
  lastMod: number;
  // false while the scan of the directory goes on
  done: boolean;
  // a directory under it could not be read: the numbers leave it out
  failed: boolean;
}

// What a finished scan found under a directory.
interface Total {
  size: number;
  lastMod: number;
}

// Totals of the directories of one file system, by path, kept so that
// coming back to a directory, or going into one, does not read again what
// was read. Only totals of scans that finished without errors are kept.
export class DirStatsCache {
  private totals: Map<string, Total> = new Map();

  get(path: string): Total | undefined {
    return this.totals.get(path);
  }

  set(path: string, total: Total) {
    this.totals.set(path, total);
  }

  // Something changed in the directory: its total and the totals of the
  // directories it is in are not right any more.
  invalidatePath(path: string) {
    while (true) {
      this.totals.delete(path);
      let parent = filepath.dir(path);
      if (path == "/" || path == "" || parent == path) {
        return;
      }
      path = parent || "/";
    }
  }

  // Also forgets everything under the directory, for a scan from scratch.
  invalidateTree(path: string) {
    this.invalidatePath(path);
    let prefix = path.endsWith("/") ? path : path + "/";
    for (let p of [...this.totals.keys()]) {
      if (p.startsWith(prefix)) {
        this.totals.delete(p);
      }
    }
  }

  get count(): number {
    return this.totals.size;
  }
}

export interface DirScan {
  cancel: () => void;
  // settles when the scan is over or was cancelled
  finished: Promise<void>;
}

export interface DirScanOpts {
  // reads one directory; throws if it cannot
  readDir: (path: string) => Promise<FsDir>;
  // the directory shown
  dirPath: string;
  // the names of the directories in it
  names: string[];
  // called when more is known about a directory, and when it is done
  onUpdate: (name: string, stat: DirStat) => void;
  cache?: DirStatsCache;
  // how many directories are read at the same time
  concurrency?: number;
}

// a tree deeper than this is a loop
const maxDepth = 100;

// Starts finding the size and the date of the directories with the given
// names in a directory.
export function scanDirStats(opts: DirScanOpts): DirScan {
  let cancelled = false;
  let limit = opts.concurrency ?? 3;
  let running = 0;
  // the newest request is served first, which goes down one directory
  // before starting the next: the first ones listed are done first
  let waiting: (() => void)[] = [];

  function acquire(): Promise<void> {
    if (running < limit) {
      running++;
      return Promise.resolve();
    }
    return new Promise((resolve) => waiting.push(resolve));
  }

  function release() {
    let next = waiting.pop();
    if (next) {
      next();
    } else {
      running--;
    }
  }

  // Returns the total of the directory, or null if it is not known: the
  // scan was cancelled or something under it could not be read.
  async function subtree(path: string, depth: number, stat: DirStat, report: () => void): Promise<Total | null> {
    let cached = opts.cache?.get(path);
    if (cached) {
      stat.size += cached.size;
      stat.lastMod = Math.max(stat.lastMod, cached.lastMod);
      report();
      return cached;
    }
    if (depth > maxDepth) {
      stat.failed = true;
      return null;
    }
    await acquire();
    let dir: FsDir;
    try {
      if (cancelled) {
        return null;
      }
      dir = await opts.readDir(path);
    } catch (e) {
      console.log("scanDirStats: cannot read", path, e);
      stat.failed = true;
      return null;
    } finally {
      release();
    }
    if (cancelled) {
      return null;
    }
    let total: Total = { size: 0, lastMod: 0 };
    let subDirs: string[] = [];
    let n = dir.count();
    for (let i = 0; i < n; i++) {
      if (dir.entryIsDir(i)) {
        subDirs.push(dir.entryName(i));
        continue;
      }
      let size = dir.entrySize(i);
      if (size > 0) {
        total.size += size;
      }
      total.lastMod = Math.max(total.lastMod, dir.entryModTime(i) || 0);
    }
    stat.size += total.size;
    stat.lastMod = Math.max(stat.lastMod, total.lastMod);
    report();

    // in reverse: the newest request is served first
    let pending: Promise<Total | null>[] = [];
    for (let i = subDirs.length - 1; i >= 0; i--) {
      pending.push(subtree(filepath.join(path, subDirs[i]), depth + 1, stat, report));
    }
    let complete = true;
    for (let sub of await Promise.all(pending)) {
      if (!sub) {
        complete = false;
        continue;
      }
      total.size += sub.size;
      total.lastMod = Math.max(total.lastMod, sub.lastMod);
    }
    if (!complete) {
      return null;
    }
    opts.cache?.set(path, total);
    return total;
  }

  async function scanOne(name: string) {
    let stat: DirStat = { size: 0, lastMod: 0, done: false, failed: false };
    let report = () => {
      if (!cancelled) {
        opts.onUpdate(name, { ...stat });
      }
    };
    await subtree(filepath.join(opts.dirPath, name), 0, stat, report);
    stat.done = true;
    report();
  }

  let all: Promise<void>[] = [];
  for (let i = opts.names.length - 1; i >= 0; i--) {
    all.push(scanOne(opts.names[i]));
  }
  return {
    cancel() {
      cancelled = true;
    },
    finished: Promise.all(all).then(() => {}),
  };
}
